import { atom } from 'jotai'
import { atomWithStorage, createJSONStorage, RESET } from 'jotai/utils'

// Create a custom storage object with proper typing
const createCustomStorage = <T>() => {
  const inMemoryStorage = new Map<string, string>()

  return createJSONStorage<T>(() => ({
    getItem: (key: string): string | null => {
      try {
        return localStorage.getItem(key)
      } catch {
        return inMemoryStorage.get(key) ?? null
      }
    },
    setItem: (key: string, newValue: string) => {
      try {
        localStorage.setItem(key, newValue)
      } catch {
        inMemoryStorage.set(key, newValue)
      }
    },
    removeItem: (key: string) => {
      try {
        localStorage.removeItem(key)
      } catch {
        inMemoryStorage.delete(key)
      }
    },
  }))
}

// Utility function to create atoms with safe storage and generic type support
export function createAtomWithSafeStorage<T>(key: string, initialValue: T) {
  const customStorage = createCustomStorage<T>()
  const baseAtom = atomWithStorage<T>(key, initialValue, customStorage)
  
  const safeAtom = atom(
    (get) => {
      try {
        return get(baseAtom)
      } catch (error) {
        // console.error(`Error reading atom ${key}:`, error)
        return initialValue
      }
    },
    (_get, set, update: T | typeof RESET) => {
      try {
        set(baseAtom, update)
      } catch (error) {
        // console.error(`Error updating atom ${key}:`, error)
      }
    }
  )

  return safeAtom
}